import { Tooltip } from "@mantine/core";
import { IconEscalatorUp, IconMailOff, IconPencil } from "@tabler/icons-react";
import {
  type ReactNode,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

type ReceivablesDraftReadyIconProps = {
  show: boolean;
  title?: string;
};

/**
 * Shown at the start of the invoice-number cell when a drafted email is
 * waiting to go out for that invoice.
 */
export function ReceivablesDraftReadyIcon({
  show,
  title = "Email draft ready to send",
}: ReceivablesDraftReadyIconProps) {
  if (!show) return null;

  return (
    <span
      className="inline-flex shrink-0 text-emerald-600"
      title={title}
      aria-label={title}
      role="img"
    >
      <IconPencil size={15} stroke={1.9} />
    </span>
  );
}

type ReceivablesUnreadDotProps = {
  show: boolean;
};

export function ReceivablesUnreadDot({ show }: ReceivablesUnreadDotProps) {
  if (!show) return null;

  return (
    <span
      className="h-2 w-2 shrink-0 rounded-full bg-blue-500"
      title="Unread email"
      aria-hidden="true"
    />
  );
}

type ReceivablesEmailDisabledIconProps = {
  disabled: boolean;
  title?: string;
};

export function ReceivablesEmailDisabledIcon({
  disabled,
  title = "Email sending disabled",
}: ReceivablesEmailDisabledIconProps) {
  if (!disabled) return null;

  return (
    <span
      className="inline-flex h-8 w-8 items-center justify-center text-slate-400"
      title={title}
      aria-label={title}
    >
      <IconMailOff size={18} stroke={1.75} />
    </span>
  );
}

type ReceivablesEscalationIconProps = {
  show: boolean;
  title?: string;
};

export function ReceivablesEscalationIcon({
  show,
  title = "Invoice escalated",
}: ReceivablesEscalationIconProps) {
  if (!show) return null;

  return (
    <span
      className="inline-flex h-8 w-8 items-center justify-center text-indigo-600"
      title={title}
      aria-label={title}
    >
      <IconEscalatorUp size={18} stroke={1.75} />
    </span>
  );
}

type ReceivablesCustomerContactHintProps = {
  name?: string;
  email?: string;
  phone?: string | null;
  children: ReactNode;
};

export function ReceivablesCustomerContactHint({
  name,
  email,
  phone,
  children,
}: ReceivablesCustomerContactHintProps) {
  const trimmedName = name?.trim() ?? "";
  const trimmedEmail = email?.trim() ?? "";
  const trimmedPhone = phone?.trim() ?? "";
  const wrapRef = useRef<HTMLSpanElement>(null);
  const [nameTruncated, setNameTruncated] = useState(false);

  useLayoutEffect(() => {
    const root = wrapRef.current;
    if (!root || !trimmedName) {
      setNameTruncated(false);
      return;
    }

    const target =
      (root.querySelector(".truncate") as HTMLElement | null) ?? root;

    const update = () => {
      setNameTruncated(target.scrollWidth - target.clientWidth > 1);
    };
    update();

    if (typeof ResizeObserver === "undefined") {
      return;
    }

    const observer = new ResizeObserver(update);
    observer.observe(target);
    window.addEventListener("resize", update);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [trimmedName]);

  const showName = Boolean(trimmedName && nameTruncated);
  const hasContact = Boolean(trimmedEmail || trimmedPhone);

  return (
    <span ref={wrapRef} className="inline-flex min-w-0 max-w-full">
      <Tooltip
        disabled={!showName && !hasContact}
        label={
          <span className="flex flex-col gap-0.5 text-left text-xs font-normal no-underline">
            {showName ? (
              <span className="font-semibold text-[#111827]">{trimmedName}</span>
            ) : null}
            {trimmedEmail ? (
              <span className="text-[#111827]">{trimmedEmail}</span>
            ) : null}
            {trimmedPhone ? (
              <span className="text-[#6B7280]">{trimmedPhone}</span>
            ) : null}
          </span>
        }
        position="bottom-start"
        withArrow
        multiline
        openDelay={120}
        zIndex={400}
        events={{ hover: true, focus: true, touch: true }}
        styles={{
          tooltip: {
            backgroundColor: "#FFFFFF",
            color: "#111827",
            border: "1px solid #E5E7EB",
            boxShadow: "0 8px 20px rgba(17, 24, 39, 0.08)",
            padding: "8px 10px",
          },
        }}
      >
        <span className="inline-flex min-w-0 max-w-full">{children}</span>
      </Tooltip>
    </span>
  );
}
